import { useState, useRef } from 'react';
import { convertToMp3, getBestSupportedMimeType } from '../utils/audioConverter';

interface CallRecordingData {
  recordingUrl?: string;
  audioFile?: File | null;
  audioBlob?: Blob;
  duration: number;
  callTime: string;
  phoneNumber: string;
}

export const useRecording = (phoneNumber: string) => {
  const [recordingData, setRecordingData] =
    useState<CallRecordingData | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const destinationRef =
    useRef<MediaStreamAudioDestinationNode | null>(null);

  /**
   * START RECORDING
   * @param localStream microphone stream
   * @param remoteStreams ACS remote audio streams
   */
  const startRecording = async (
    localStream: MediaStream,
    remoteStreams: MediaStream[]
  ) => {
    try {
      // Create AudioContext
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const destination =
        audioContext.createMediaStreamDestination();
      destinationRef.current = destination;

      // Local microphone
      const localSource =
        audioContext.createMediaStreamSource(localStream);
      localSource.connect(destination);

      // Remote ACS participants
      remoteStreams.forEach(remoteStream => {
        const remoteSource =
          audioContext.createMediaStreamSource(remoteStream);
        remoteSource.connect(destination);
      });

      // MediaRecorder on MIXED stream
      const mimeType = getBestSupportedMimeType();
      const recorder = new MediaRecorder(destination.stream, {
        mimeType
      });

      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = e => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = async () => {
        if (!audioChunksRef.current.length) return;

        const blob = new Blob(audioChunksRef.current, {
          type: recorder.mimeType
        });

        const mp3 = await convertToMp3(blob);

        const file = new File([mp3.blob], mp3.fileName, {
          type: mp3.format
        });

        setRecordingData({
          audioBlob: mp3.blob,
          audioFile: file,
          duration: 0,
          callTime: new Date().toISOString(),
          phoneNumber
        });

        audioChunksRef.current = [];
      };

      recorder.start();
      console.log('Recording started (mixed audio)');

    } catch (err) {
      console.error('Recording failed:', err);
      stopRecording();
      throw err;
    }
  };

  /**
   * STOP RECORDING
   */
  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;

    if (recorder?.state === 'recording') {
      recorder.stop();
    }

    audioContextRef.current?.close();
    audioContextRef.current = null;
    mediaRecorderRef.current = null;
  };

  return {
    recordingData,
    startRecording,
    stopRecording,
    setRecordingData
  };
};
